/**
 * View Mode Hook
 * Manages operator/tourist view mode preference
 */

import { useState, useEffect, useCallback } from 'react';

export type ViewMode = 'operator' | 'tourist';

const STORAGE_KEY = 'viewMode';

interface UseViewModeReturn {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  toggleViewMode: () => void;
  isOperatorMode: boolean;
  isTouristMode: boolean;
}

export function useViewMode(defaultMode: ViewMode = 'operator'): UseViewModeReturn {
  const [viewMode, setViewModeState] = useState<ViewMode>(defaultMode);
  const [isHydrated, setIsHydrated] = useState(false);

  // Load view mode from localStorage on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY) as ViewMode | null;
      if (stored === 'operator' || stored === 'tourist') {
        setViewModeState(stored);
      } else {
        setViewModeState(defaultMode);
      }
      setIsHydrated(true);
    }
  }, [defaultMode]);

  // Set view mode and persist to localStorage
  const setViewMode = useCallback((mode: ViewMode) => {
    setViewModeState(mode);
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, mode);
    }
  }, []);

  // Toggle between operator and tourist mode
  const toggleViewMode = useCallback(() => {
    setViewMode(viewMode === 'operator' ? 'tourist' : 'operator');
  }, [viewMode, setViewMode]);

  return {
    viewMode: isHydrated ? viewMode : defaultMode,
    setViewMode,
    toggleViewMode,
    isOperatorMode: viewMode === 'operator',
    isTouristMode: viewMode === 'tourist',
  };
}
